import { fetch } from "../lib/network";
import AppSettings from "../config";
import queryString from "query-string";
import networkSelectors from "../selectors/network";
import { createAction } from "redux-api-middleware";
import { Dispatch } from "redux";
import { coreService } from "../services/coreService";
import { MultiPartFinalizationResponse } from "./multipartUpload";

export const UPLOAD_LOADING = "NEW_UPLOAD_LOADING";
export const UPLOAD_LOADED = "NEW_UPLOAD_LOADED";
export const UPLOAD_FAILED = "NEW_UPLOAD_FAILED";
export const enum FileUsage {
  Permits = "Permits",
  PermitsEditorMedia = "PermitsEditorMedia",
  CE = "CE",
  CRM = "CRM",
  CRMAttachments = "CRMAttachments",
  InspectionItemCommentAttachments = "InspectionItemCommentAttachments",
  SiteAssets = "SiteAssets",
  NotifyVoiceResources = "NotifyVoiceResources",
  NotifyEditorMedia = "NotifyEditorMedia",
}

// Was the previous usage folder for legacy CRM attachments before the core file system
export const LEGACY_CRM_ATTACHMENT_USAGE_KEY = "crm" as const;

function loadingCoreUpload() {
  return {
    type: UPLOAD_LOADING,
  };
}

function loadedCoreUpload(id: string, json: FileFinalizeResponse) {
  return {
    type: UPLOAD_LOADED,
    uploadId: id,
    payload: json,
  };
}

function failedCoreUpload() {
  return {
    type: UPLOAD_FAILED,
  };
}

export type FileUploadResponse = Awaited<ReturnType<typeof coreService.fileCreate>>["data"];
function newCoreUpload(dispatch: Dispatch, file: File, usage: FileUsage, usageOverride?: string) {
  const query = queryString.stringify({
    usage,
    ...(usageOverride ? { customUsageKey: usageOverride } : {}),
    fileName: file.name,
    mimeType: file.type,
  });

  const resp = dispatch(
    createAction({
      endpoint: `${AppSettings.FILE_UPLOAD_API_URL}?${query}`,
      method: "POST",
      headers: networkSelectors.defaultHeaders,
      types: [UPLOAD_LOADING, UPLOAD_LOADED, UPLOAD_FAILED],
      body: JSON.stringify({}),
    }),
  );

  return resp.then((response) => response.payload as FileUploadResponse);
}

function performCoreUpload(json: FileUploadResponse, file: File) {
  const options = {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type,
    },
  };
  return fetch(`${json.url}`, options).then((response) => {
    return {
      ...response.json(),
      uuid: json.uuid,
    };
  });
}

export type FileFinalizeResponse = Awaited<ReturnType<typeof coreService.fileFinalizedCreate>>["data"];
export function finalizeCoreUpload(dispatch: Dispatch, uuid: string) {
  const resp = dispatch(
    createAction({
      endpoint: `${AppSettings.FILE_UPLOAD_API_URL}/${uuid}/finalized`,
      method: "POST",
      headers: networkSelectors.defaultHeaders,
      types: [UPLOAD_LOADING, UPLOAD_LOADED, UPLOAD_FAILED],
      body: JSON.stringify({}),
    }),
  );

  return resp.then((response) => {
    return {
      ...(response.payload as FileFinalizeResponse),
      uuid,
    };
  });
}

/**
 * This creates a temporary file, which you must then commit on save, or it will be deleted.
 */
export function coreUploadTempFile({
  id,
  file,
  usage,
  usageOverride,
}: {
  id: string;
  file: File;
  usage?: FileUsage;
  usageOverride?: string;
}) {
  if (!usage) throw new Error("usage is required when using coreUpload");
  return (dispatch: Dispatch) => {
    dispatch(loadingCoreUpload());
    return newCoreUpload(dispatch, file, usage as FileUsage, usageOverride)
      .then((json) => performCoreUpload(json, file))
      .then((json) => finalizeCoreUpload(dispatch, json.uuid))
      .then((json) => dispatch(loadedCoreUpload(id, json)))
      .catch(() => dispatch(failedCoreUpload()));
  };
}

export type FileUploadFinalizeResponse =
  | (FileFinalizeResponse & { uuid: string })
  | (MultiPartFinalizationResponse & { uuid: string });
